import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Search, UserPlus, Users, Check } from "lucide-react";
import { api } from "@/lib/api";
import { inputCls } from "@/components/common";
import { CustomerDialog } from "@/components/dialogs/CustomerDialog";

export function CustomerPicker({ value, onChange, testId = "customer-picker" }) {
  const { data: customers = [] } = useQuery({ queryKey: ["customers"], queryFn: () => api.get("/customers").then((r) => r.data) });
  const [mode, setMode] = useState("existing");
  const [q, setQ] = useState("");
  const [adding, setAdding] = useState(false);
  const selected = customers.find((c) => c.id === value);
  useEffect(() => { if (selected) setQ(""); }, [selected]);
  const list = useMemo(() => {
    const s = q.trim().toLowerCase();
    const src = s ? customers.filter((c) => `${c.name} ${c.phone} ${c.email} ${c.service_address}`.toLowerCase().includes(s)) : customers;
    return src.slice(0, 8);
  }, [customers, q]);
  const pick = (c) => { onChange(c); setQ(""); };

  return (
    <div className="space-y-2" data-testid={testId}>
      <div className="grid grid-cols-2 gap-1 p-1 rounded-xl bg-slate-100 text-sm font-semibold">
        <button type="button" onClick={() => setMode("existing")} className={`h-9 rounded-lg flex items-center justify-center gap-1.5 ${mode === "existing" ? "bg-white shadow-sm text-slate-900" : "text-slate-500"}`} data-testid={`${testId}-existing-tab`}><Users size={15} /> Choose Existing</button>
        <button type="button" onClick={() => { setMode("new"); setAdding(true); }} className={`h-9 rounded-lg flex items-center justify-center gap-1.5 ${mode === "new" ? "bg-white shadow-sm text-orange-600" : "text-slate-500"}`} data-testid={`${testId}-new-tab`}><UserPlus size={15} /> + Add New Customer</button>
      </div>
      {selected ? (
        <div className="flex items-center gap-3 rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2.5" data-testid={`${testId}-selected`}>
          <span className="w-8 h-8 rounded-full bg-emerald-600 text-white grid place-items-center"><Check size={16} /></span>
          <div className="min-w-0 flex-1"><div className="font-semibold text-sm truncate">{selected.name}</div><div className="text-xs text-slate-500 truncate">{[selected.phone, selected.service_address].filter(Boolean).join(" · ") || "No contact details"}</div></div>
          <button type="button" onClick={() => { onChange(null); setMode("existing"); }} className="text-xs font-semibold text-slate-600 underline" data-testid={`${testId}-change`}>Change</button>
        </div>
      ) : (
        <div className="rounded-xl border border-slate-200 overflow-hidden">
          <div className="relative"><Search size={15} className="absolute left-3 top-3.5 text-slate-400" /><input data-testid={`${testId}-search`} className={inputCls + " pl-9 rounded-none border-0 border-b"} placeholder="Search name, phone, address" value={q} onChange={(e) => setQ(e.target.value)} autoComplete="off" /></div>
          <div className="max-h-52 overflow-y-auto divide-y divide-slate-100" data-testid={`${testId}-results`}>
            {list.map((c) => (
              <button type="button" key={c.id} onClick={() => pick(c)} className="w-full text-left px-3 py-2.5 hover:bg-orange-50 flex items-center gap-3" data-testid={`${testId}-option-${c.id}`}>
                <span className="w-8 h-8 rounded-full bg-[#0B192C] text-white grid place-items-center text-xs font-bold shrink-0">{c.name.split(" ").map((s) => s[0]).join("").slice(0, 2)}</span>
                <span className="min-w-0"><span className="block text-sm font-semibold truncate">{c.name}</span><span className="block text-xs text-slate-500 truncate">{[c.phone, c.service_address].filter(Boolean).join(" · ")}</span></span>
              </button>
            ))}
            {list.length === 0 && (
              <div className="px-3 py-4 text-sm text-slate-500 text-center" data-testid={`${testId}-empty`}>
                {customers.length === 0 ? "No customers yet." : "No match."}{" "}
                <button type="button" onClick={() => { setMode("new"); setAdding(true); }} className="font-semibold text-orange-600 underline" data-testid={`${testId}-empty-add`}>Add new customer</button>
              </div>
            )}
          </div>
        </div>
      )}
      <CustomerDialog open={adding} onClose={() => { setAdding(false); if (!selected) setMode("existing"); }} checkDuplicates
        onSaved={(c) => { pick(c); setMode("existing"); }} onSelectExisting={(c) => { pick(c); setMode("existing"); }} />
    </div>
  );
}
